import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  CanvasApi,
  extractWidgetData,
  extractWidgetListData,
} from './canvasApi';
import {
  Widget,
  CreateWidgetRequest,
  UpdateWidgetRequest,
} from '../../types/canvas.types';

// Query keys
export const widgetKeys = {
  all: ['widgets'] as const,
  lists: () => [...widgetKeys.all, 'list'] as const,
  list: (accountId: string, pageId: string) =>
    [...widgetKeys.lists(), accountId, pageId] as const,
  details: () => [...widgetKeys.all, 'detail'] as const,
  detail: (accountId: string, widgetId: string) =>
    [...widgetKeys.details(), accountId, widgetId] as const,
};

// Hook to fetch all widgets for a page
export function useWidgets(accountId: string, pageId: string) {
  return useQuery({
    queryKey: widgetKeys.list(accountId, pageId),
    queryFn: async () => {
      const response = await CanvasApi.listWidgets(accountId, pageId).run();
      return extractWidgetListData(response);
    },
    enabled: !!accountId && !!pageId,
    staleTime: 1000 * 60 * 2, // 2 minutes (shorter for more frequent updates)
  });
}

// Hook to fetch a single widget
export function useWidget(widgetId: string, accountId?: string) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];

  return useQuery({
    queryKey: widgetKeys.detail(finalAccountId!, widgetId),
    queryFn: async () => {
      const response = await CanvasApi.getWidget(
        finalAccountId!,
        widgetId,
      ).run();
      return extractWidgetData(response);
    },
    enabled: !!finalAccountId && !!widgetId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// Hook to create a new widget
export function useCreateWidget(pageId: string, accountId?: string) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateWidgetRequest) => {
      const response = await CanvasApi.createWidget(
        finalAccountId!,
        pageId,
        data,
      ).run();
      return extractWidgetData(response);
    },
    onSuccess: (newWidget) => {
      // Invalidate and refetch widgets list
      queryClient.invalidateQueries({
        queryKey: widgetKeys.list(finalAccountId!, pageId),
      });

      // Optimistically update the cache
      queryClient.setQueryData(
        widgetKeys.detail(finalAccountId!, newWidget.id),
        newWidget,
      );
    },
  });
}

// Hook to update a widget
export function useUpdateWidget(
  widgetId: string,
  pageId: string,
  accountId?: string,
) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateWidgetRequest) => {
      const response = await CanvasApi.updateWidget(
        finalAccountId!,
        widgetId,
        data,
      ).run();
      return extractWidgetData(response);
    },
    onSuccess: (updatedWidget) => {
      // Update the specific widget in cache
      queryClient.setQueryData(
        widgetKeys.detail(finalAccountId!, widgetId),
        updatedWidget,
      );

      // Update the widget in the list
      queryClient.setQueryData(
        widgetKeys.list(finalAccountId!, pageId),
        (oldData: Widget[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((widget) =>
            widget.id === widgetId ? updatedWidget : widget,
          );
        },
      );
    },
  });
}

// Hook to delete a widget
export function useDeleteWidget(pageId: string, accountId?: string) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (widgetId: string) => {
      await CanvasApi.deleteWidget(finalAccountId!, widgetId).run();
      return widgetId;
    },
    onSuccess: (deletedWidgetId) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: widgetKeys.detail(finalAccountId!, deletedWidgetId),
      });

      // Update the list
      queryClient.setQueryData(
        widgetKeys.list(finalAccountId!, pageId),
        (oldData: Widget[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.filter((widget) => widget.id !== deletedWidgetId);
        },
      );
    },
  });
}

// Hook to duplicate a widget
export function useDuplicateWidget(pageId: string, accountId?: string) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (widgetId: string) => {
      const response = await CanvasApi.duplicateWidget(
        finalAccountId!,
        widgetId,
      ).run();
      return extractWidgetData(response);
    },
    onSuccess: (duplicatedWidget) => {
      // Invalidate and refetch widgets list to show new widget
      queryClient.invalidateQueries({
        queryKey: widgetKeys.list(finalAccountId!, pageId),
      });

      // Add to cache
      queryClient.setQueryData(
        widgetKeys.detail(finalAccountId!, duplicatedWidget.id),
        duplicatedWidget,
      );
    },
  });
}

// Hook for bulk updating widget positions (for drag and drop)
export function useBulkUpdateWidgetPositions(
  pageId: string,
  accountId?: string,
) {
  const params = useParams();
  const finalAccountId = accountId || params['accountId'];
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: Array<{ widgetId: string; position: any }>) => {
      // Update each widget position
      const promises = updates.map(({ widgetId, position }) =>
        CanvasApi.updateWidget(finalAccountId!, widgetId, { position }).run(),
      );

      const responses = await Promise.all(promises);
      return responses.map(extractWidgetData);
    },
    onSuccess: (updatedWidgets) => {
      // Update each widget in cache
      updatedWidgets.forEach((widget) => {
        queryClient.setQueryData(
          widgetKeys.detail(finalAccountId!, widget.id),
          widget,
        );
      });

      // Update the widgets list
      queryClient.setQueryData(
        widgetKeys.list(finalAccountId!, pageId),
        (oldData: Widget[] | undefined) => {
          if (!oldData) return oldData;

          const updatedMap = new Map(updatedWidgets.map((w) => [w.id, w]));
          return oldData.map((widget) =>
            updatedMap.has(widget.id) ? updatedMap.get(widget.id)! : widget,
          );
        },
      );
    },
  });
}
